import { ReactElement } from 'react';

import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  Link,
  Stack,
  Text,
  Tooltip,
  useColorModeValue,
} from '@ecoinvent/ui';
import { format, parseISO } from 'date-fns';
import mixpanel from 'mixpanel-browser';
import { BiChevronDown, BiChevronUp } from 'react-icons/bi';
import { MdChevronRight } from 'react-icons/md';
import { Link as RouterLink, useLocation, useParams } from 'react-router';

import { DatasetRef, useCanCompare } from 'components/shared/Compare';
import { DatasetHistoryDetail } from 'hooks/data/datasets/types';
import useReleaseVersions from 'hooks/data/versions/useReleaseVersions';
import useVersionInfo from 'hooks/useVersionInfo';
import { useDatasetSession } from 'pages/Dataset/DatasetSessionContext';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import { historyComparePath } from '../historyCompare';

const VIEWING_THIS_VERSION = "You're viewing this version";
const NO_DATASET_IN_RELEASE = 'No dataset in this release';

export type ReleaseDatasets = { version: string; label: string; datasets: DatasetHistoryDetail[] };

/** Disabled buttons fire no pointer events, so the tooltip needs its own wrapper to open on hover. */
const DisabledReason = ({ reason, children }: { reason?: string; children: ReactElement }) =>
  reason ? (
    <Tooltip label={reason} hasArrow shouldWrapChildren>
      {children}
    </Tooltip>
  ) : (
    children
  );

type CompareActionProps = { to: string; disabledReason?: string; fontSize: 'lg' | 'md'; onClick: () => void };

const CompareAction = ({ to, disabledReason, fontSize, onClick }: CompareActionProps) => {
  const color = useColorModeValue('gray.800', 'whiteAlpha.900');
  const { pathname, search } = useLocation();
  const style = { variant: 'link', color, fontSize, lineHeight: fontSize === 'lg' ? 7 : 6 } as const;

  if (disabledReason) {
    return (
      <DisabledReason reason={disabledReason}>
        <Button {...style} isDisabled>
          Compare
        </Button>
      </DisabledReason>
    );
  }

  return (
    <Button as={RouterLink} to={to} state={{ entry: 'history', from: `${pathname}${search}` }} {...style} onClick={onClick}>
      Compare
    </Button>
  );
};

type ViewActionProps = { href: string; disabledReason?: string; onClick: () => void };

const ViewAction = ({ href, disabledReason, onClick }: ViewActionProps) => {
  const style = {
    variant: 'link',
    colorScheme: 'green',
    fontSize: 'lg',
    lineHeight: 7,
    rightIcon: <Icon as={MdChevronRight} boxSize={4} />,
  } as const;

  if (disabledReason) {
    return (
      <DisabledReason reason={disabledReason}>
        <Button {...style} isDisabled>
          View Dataset
        </Button>
      </DisabledReason>
    );
  }

  return (
    <Button as="a" href={href} {...style} onClick={onClick}>
      View Dataset
    </Button>
  );
};

type Props = {
  version: string;
  title: string;
  description: string;
  groups: ReleaseDatasets[];
  hasDetails: boolean;
  hasConnector: boolean;
};

const HistoryEntry = ({ version, title, description, groups, hasDetails, hasConnector }: Props) => {
  const { spold_id } = useParams();
  const { ver, system_model } = useVersionInfo();
  const { increment } = useDatasetSession();
  const canCompare = useCanCompare();
  const { data: releases } = useReleaseVersions();

  const panelBg = useColorModeValue('gray.100', 'gray.900');
  const tablesBg = useColorModeValue('white', 'gray.800');
  const tablesBorderColor = useColorModeValue('transparent', 'whiteAlpha.200');
  const releaseDateColor = useColorModeValue('gray.500', 'gray.400');
  const connectorColor = useColorModeValue('gray.400', 'gray.600');
  const detailsColor = useColorModeValue('gray.800', 'whiteAlpha.900');

  const isViewedRelease = version === ver;
  const releaseDate = releases?.[version]?.find((release) => release.system_model === system_model)?.release_date;

  const viewed: DatasetRef = { id: spold_id ?? '', version: ver, systemModel: system_model };
  const refFor = (dataset: DatasetHistoryDetail, release: string): DatasetRef => ({
    id: String(dataset.id),
    version: release,
    systemModel: system_model,
  });
  const datasetPath = (dataset: DatasetHistoryDetail, release: string) => `/${release}/${system_model}/dataset/${dataset.id}`;

  const track = (action: 'view' | 'compare', dataset: DatasetHistoryDetail, release: string) => {
    mixpanel.track(MIXPANEL_EVENTS.DATASET_HISTORY_USED, {
      spold_id,
      action,
      targetVersion: release,
      targetSystemModel: system_model,
      targetSpoldId: dataset.id,
      targetName: dataset.activity,
    });
    increment('history_tab_action_count');
  };

  const target = groups[0]?.datasets[0];
  const rowDisabledReason = !target ? NO_DATASET_IN_RELEASE : isViewedRelease ? VIEWING_THIS_VERSION : undefined;

  return (
    <Accordion allowToggle>
      <AccordionItem border="none">
        {({ isExpanded }) => (
          <>
            <Grid
              templateColumns={{ base: '96px minmax(0, 1fr)', lg: '96px minmax(0, 1fr) auto' }}
              templateAreas={{ base: '"badge details" "badge actions"', lg: '"badge details actions"' }}
              columnGap={{ base: 4, lg: 8 }}
              rowGap={2}
              alignItems="center"
            >
              <Flex gridArea="badge" direction="column" align="center" alignSelf={{ base: 'stretch', lg: 'center' }}>
                <Stack
                  w="96px"
                  h="72px"
                  flexShrink={0}
                  align="center"
                  justify="center"
                  borderRadius="xl"
                  bg={isViewedRelease ? 'green.500' : undefined}
                >
                  <Text fontSize="2xl" fontWeight="semibold" lineHeight="8" color={isViewedRelease ? 'white' : undefined}>
                    {version}
                  </Text>
                </Stack>
                {hasConnector && <Box display={{ base: 'block', lg: 'none' }} flex={1} w="4px" mt={2} bg={connectorColor} />}
              </Flex>
              <Stack gridArea="details" spacing={0}>
                <Text fontSize="lg" fontWeight="semibold" lineHeight="7">
                  {title}
                </Text>
                {/* parseISO, not new Date: a date-only value would be read as UTC and print a day early west of it. */}
                {releaseDate && (
                  <Text fontSize="sm" lineHeight="5" color={releaseDateColor} pb={1}>
                    Released {format(parseISO(releaseDate), 'PP')}
                  </Text>
                )}
                <Text lineHeight="6">{description}</Text>
              </Stack>
              <Flex gridArea="actions" justifySelf={{ base: 'start', lg: 'end' }} columnGap={4} rowGap={1} flexWrap="wrap">
                {hasDetails ? (
                  <AccordionButton
                    w="auto"
                    p={0}
                    _hover={{ bg: 'none' }}
                    fontSize="lg"
                    fontWeight="semibold"
                    lineHeight="7"
                    color={detailsColor}
                  >
                    Show Details <Icon as={isExpanded ? BiChevronUp : BiChevronDown} color="green.500" ml={1} />
                  </AccordionButton>
                ) : (
                  <>
                    {canCompare(version) && (
                      <CompareAction
                        to={target ? historyComparePath(viewed, refFor(target, version)) : ''}
                        disabledReason={rowDisabledReason}
                        fontSize="lg"
                        onClick={() => target && track('compare', target, version)}
                      />
                    )}
                    <ViewAction
                      href={target ? datasetPath(target, version) : ''}
                      disabledReason={rowDisabledReason}
                      onClick={() => target && track('view', target, version)}
                    />
                  </>
                )}
              </Flex>
            </Grid>

            <AccordionPanel p={{ base: 4, md: 8 }} bg={panelBg} borderRadius="md" mt={4}>
              <Stack
                spacing={5}
                bg={tablesBg}
                p={{ base: 4, md: 8 }}
                borderRadius="md"
                border="1px solid"
                borderColor={tablesBorderColor}
              >
                {groups.map((group) => (
                  <Stack key={group.label} spacing={2}>
                    <Heading fontWeight="bold" fontSize="md">
                      {group.label}
                    </Heading>
                    {group.datasets.map((dataset) => {
                      const isViewedDataset = String(dataset.id) === spold_id && group.version === ver;
                      return (
                        <HStack key={dataset.id} spacing={4} align="baseline">
                          <Link
                            href={datasetPath(dataset, group.version)}
                            onClick={() => track('view', dataset, group.version)}
                          >
                            <Text>
                              {dataset.activity}, {dataset.product}, {dataset.geography}
                            </Text>
                          </Link>
                          {canCompare(group.version) && (
                            <CompareAction
                              to={historyComparePath(viewed, refFor(dataset, group.version))}
                              disabledReason={isViewedDataset ? VIEWING_THIS_VERSION : undefined}
                              fontSize="md"
                              onClick={() => track('compare', dataset, group.version)}
                            />
                          )}
                        </HStack>
                      );
                    })}
                  </Stack>
                ))}
              </Stack>
            </AccordionPanel>

            {hasConnector && (
              <Box w="96px" h="46px" display="flex" justifyContent="center">
                <Box w="4px" h="full" bg={connectorColor} />
              </Box>
            )}
          </>
        )}
      </AccordionItem>
    </Accordion>
  );
};

export default HistoryEntry;
